import { PointAddResponse } from "@1games-ru/region-shared/dist/client/requests/point";
import { InteriorType } from "@1games-ru/region-shared/dist/enums/interior/interior";
import { InteriorHouse } from "@1games-ru/region-shared/dist/types/interior/interior-b2";
import { useCallback, useId, useState } from "react";
import { toast } from "react-toastify";
import { CheckIcon } from "@/shared/icons";
import { Button, TextInput } from "@/shared/ui";
import { Point } from "@/domains/createEntity/constants/point";
import getInitPoint from "@/domains/createEntity/helpers/getInitPoint";
import { pointToPoint } from "@/domains/createEntity/helpers/pointToPoint";
import useGetInteriors from "@/domains/createEntity/hooks/useGetInteriors";
import usePoint from "@/domains/createEntity/hooks/usePoint";
import type { InteriorFormProps } from "@/domains/createEntity/containers/forms/interior/CrateEntityInteriorFormContainer";
import CreateEntityRow from "@/domains/createEntity/components/CreateEntityRow";
import CreateEntitySetLocation from "@/domains/createEntity/components/CreateEntitySetLocation";
import CreateEntitySlider from "@/domains/createEntity/components/CreateEntitySlider";

export function CreateEntityInteriorHouseFormContainer({
  onSubmit,
  onClear,
}: InteriorFormProps<InteriorHouse>) {
  const id = useId();
  const { addPoint } = usePoint();
  const [uuid, setUuid] = useState<string | null>(null);
  const [name, setName] = useState<string>("");
  const [zone, setZone] = useState(30);
  const [point, setPoint] = useState<PointAddResponse | null>(null);
  const [description, setDescription] = useState<string>("");
  const [spawnPoint, setSpawnPoint] = useState<PointAddResponse | null>(null);
  const [streetTransitPoint, setStreetTransitPoint] = useState<PointAddResponse | null>(null);
  const [garageTransitPoint, setGarageTransitPoint] = useState<PointAddResponse | null>(null);
  const [safePoint, setSafePoint] = useState<PointAddResponse | null>(null);
  const [wardrobePoint, setWardrobePoint] = useState<PointAddResponse | null>(null);
  const [kitchenPoint, setKitchenPoint] = useState<PointAddResponse | null>(null);

  const { getInteriors } = useGetInteriors(InteriorType.House);

  const clearPoints = useCallback(() => {
    setUuid(null);
    setZone(30);
    setName("");
    setDescription("");
    setPoint(null);
    setSpawnPoint(null);
    setStreetTransitPoint(null);
    setGarageTransitPoint(null);
    setSafePoint(null);
    setWardrobePoint(null);
    setKitchenPoint(null);
    onClear();
  }, [onClear]);

  const createAndSetPoint = useCallback(
    async (
      pointType: Point,
      setter: (point: PointAddResponse | null) => void,
      additionalProps?: Record<string, unknown>,
    ) => {
      const p = await addPoint({
        ...getInitPoint(pointType, { id }),
        ...additionalProps,
      });
      setter(p);
      return p;
    },
    [addPoint, id],
  );

  async function handleEdit() {
    const interiors = await getInteriors();
    const interior = interiors.find((item) => item.uuid === uuid);
    if (!interior) {
      toast.error("Интерьер не найден");
      return;
    }

    const content = interior.content as InteriorHouse;

    setName(interior.name);
    setDescription(interior.description);
    setZone(content.border.range);

    createAndSetPoint(Point.InteriorLocation, setPoint, {
      range: content.border.range,
      position: content.border.position,
    });

    createAndSetPoint(Point.Spawn, setSpawnPoint, content.spawn);
    createAndSetPoint(Point.StreetTransit, setStreetTransitPoint, content.streetTransit);
    createAndSetPoint(Point.GarageTransit, setGarageTransitPoint, content.garageTransit);
    createAndSetPoint(Point.Safe, setSafePoint, content.safe);
    createAndSetPoint(Point.Wardrobe, setWardrobePoint, content.wardrobe);
    createAndSetPoint(Point.Kitchen, setKitchenPoint, content.kitchen);
  }

  async function handleSubmit() {
    if (
      !name ||
      !description ||
      !spawnPoint ||
      !streetTransitPoint ||
      !garageTransitPoint ||
      !safePoint ||
      !wardrobePoint ||
      !kitchenPoint
    ) {
      toast.error("Заполните все поля");
      return;
    }

    await onSubmit({
      uuid,
      name,
      description: description || null,
      content: {
        border: {
          range: zone,
          position: point?.position,
        },
        spawn: pointToPoint(spawnPoint),
        streetTransit: pointToPoint(streetTransitPoint),
        garageTransit: pointToPoint(garageTransitPoint),
        safe: pointToPoint(safePoint),
        wardrobe: pointToPoint(wardrobePoint),
        kitchen: pointToPoint(kitchenPoint),
      },
    });
    clearPoints();
  }

  return (
    <>
      <Button variant="solid-outline" onClick={clearPoints}>
        Очистить точки
      </Button>

      <CreateEntityRow label="Редактировать интерьер">
        <div className="flex gap-4">
          <TextInput
            placeholder="UUID интерьера"
            value={uuid}
            onChange={(value) => setUuid(value)}
            className="w-full"
          />
          <Button onClick={() => handleEdit()} disabled={!uuid}>
            <CheckIcon size={16} />
          </Button>
        </div>
      </CreateEntityRow>

      <CreateEntityRow label="Название интерьера">
        <TextInput value={name} onChange={setName} placeholder="Придумайте название" />
      </CreateEntityRow>

      <CreateEntityRow label="Описание интерьера">
        <TextInput
          value={description}
          onChange={setDescription}
          placeholder="Придумайте описание"
        />
      </CreateEntityRow>

      <CreateEntityRow label="Размер зоны интерьера">
        <CreateEntitySlider
          value={zone}
          min={10}
          max={50}
          step={10}
          values={[
            {
              label: "10",
              value: 10,
            },
            {
              label: "20",
              value: 20,
            },
            {
              label: "30",
              value: 30,
            },
            {
              label: "40",
              value: 40,
            },
            {
              label: "50",
              value: 50,
            },
          ]}
          onChange={setZone}
          className="px-1"
          classNameSlider="cursor-pointer"
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение интерьера">
        <CreateEntitySetLocation
          is={!!point}
          onChange={async () => {
            setPoint(await addPoint(getInitPoint(Point.InteriorLocation, { range: zone, id })));
          }}
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение спауна игрока">
        <CreateEntitySetLocation
          is={!!spawnPoint}
          onChange={async () => {
            setSpawnPoint(await addPoint(getInitPoint(Point.Spawn, { id })));
          }}
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение выхода на улицу">
        <CreateEntitySetLocation
          is={!!streetTransitPoint}
          onChange={async () => {
            setStreetTransitPoint(await addPoint(getInitPoint(Point.StreetTransit, { id })));
          }}
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение входа в гараж">
        <CreateEntitySetLocation
          is={!!garageTransitPoint}
          onChange={async () => {
            setGarageTransitPoint(await addPoint(getInitPoint(Point.GarageTransit, { id })));
          }}
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение сейфа">
        <CreateEntitySetLocation
          is={!!safePoint}
          onChange={async () => {
            setSafePoint(await addPoint(getInitPoint(Point.Safe, { id })));
          }}
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение гардероба">
        <CreateEntitySetLocation
          is={!!wardrobePoint}
          onChange={async () => {
            setWardrobePoint(await addPoint(getInitPoint(Point.Wardrobe, { id })));
          }}
        />
      </CreateEntityRow>

      <CreateEntityRow label="Местоположение кухни">
        <CreateEntitySetLocation
          is={!!kitchenPoint}
          onChange={async () => {
            setKitchenPoint(await addPoint(getInitPoint(Point.Kitchen, { id })));
          }}
        />
      </CreateEntityRow>

      <Button onClick={() => handleSubmit()}>Сохранить интерьер</Button>
    </>
  );
}
